import { ReactElement } from "react";
import { Navigate, Outlet } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useManagerDepartments } from "../hooks/useManagerDepartments";
import type { Role } from "../types/roles";

interface Props {
  roles: Role | Role[];
  children?: ReactElement;
  component?: ReactElement; // Para usar con React Router v6 element prop
}

// Helper para crear rutas protegidas con React Router v6
// Este helper renderiza el componente directamente después de verificar permisos
export function createProtectedRoute(roles: Role | Role[], Component: React.ComponentType) {
  function ProtectedRoute() {
    const { user, bootstrapped } = useAuth();
    const { departments: managerDepartments, loading: loadingDepartments } = useManagerDepartments();

    // Esperar a que termine el bootstrap antes de verificar
    if (!bootstrapped) {
      return null;
    }

    if (!user) {
      return <Navigate to="/login" replace />;
    }

    const required = Array.isArray(roles) ? roles : [roles];
    const userRoles = Array.isArray(user.roles) ? user.roles : [];
    
    // PRIMERO: Verificar si tiene alguno de los roles requeridos directamente
    // Esta verificación NO necesita esperar a los managers
    const hasRolePermission = required.some((role) => userRoles.includes(role));
    
    // Si tiene el rol directo, permitir acceso inmediatamente sin esperar managers
    if (hasRolePermission) {
      return <Component />;
    }

    // SEGUNDO: Si no tiene el rol directo, verificar si es manager
    // Solo en este caso necesitamos esperar a que carguen los managers
    if (loadingDepartments) {
      return null; // Esperar a que carguen los managers
    }

    // Verificar si es manager de algún departamento requerido
    const requiredDepartments = required
      .map((role) => ROLE_TO_DEPARTMENT[role])
      .filter((dept): dept is string => !!dept);
    
    const hasManagerPermission = requiredDepartments.length > 0 && 
      managerDepartments.length > 0 &&
      requiredDepartments.some((dept) => {
        return managerDepartments.some((md) => String(md) === String(dept));
      });

    if (!hasManagerPermission) {
      return <Navigate to="/403" replace />;
    }

    // Renderizar el componente directamente
    return <Component />;
  }
  return ProtectedRoute;
}

// Mapeo de roles a departamentos
const ROLE_TO_DEPARTMENT: Record<string, string> = {
  'hr': 'hr',
  'att_familias': 'att_familias',
  'att_alumnado': 'att_alumnado',
  'att_profesorado': 'att_profesorado',
  'facturacion': 'facturacion',
  'admisiones': 'admisiones',
};

export default function RequireRole({ roles, children, component }: Props) {
  // Si se pasa component (para React Router v6), usarlo en lugar de children
  const content = component || children;
  const { user, bootstrapped } = useAuth();
  const { departments: managerDepartments, loading: loadingDepartments } = useManagerDepartments();

  // Esperar a que termine el bootstrap antes de verificar
  if (!bootstrapped) {
    return null; // O un loader si prefieres
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const required = Array.isArray(roles) ? roles : [roles];
  const userRoles = Array.isArray(user.roles) ? user.roles : [];
  
  // PRIMERO: Verificar si tiene alguno de los roles requeridos directamente
  // Esta verificación NO necesita esperar a los managers
  const hasRolePermission = required.some((role) => userRoles.includes(role));
  
  // Si tiene el rol directo, permitir acceso inmediatamente sin esperar managers
  if (hasRolePermission) {
    // Si hay content (children o component), renderizarlo
    if (content) {
      return <>{content}</>;
    }
    // Si no hay content, usar Outlet para subrutas anidadas
    return <Outlet />;
  }

  // SEGUNDO: Si no tiene el rol directo, verificar si es manager
  // Solo en este caso necesitamos esperar a que carguen los managers
  if (loadingDepartments) {
    return null; // Esperar a que carguen los managers
  }

  // Verificar si es manager de algún departamento requerido
  const requiredDepartments = required
    .map((role) => ROLE_TO_DEPARTMENT[role])
    .filter((dept): dept is string => !!dept);
  
  const hasManagerPermission = requiredDepartments.length > 0 && 
    managerDepartments.length > 0 &&
    requiredDepartments.some((dept) => {
      return managerDepartments.some((md) => String(md) === String(dept));
    });

  if (!hasManagerPermission) {
    return <Navigate to="/403" replace />;
  }
  
  // Si hay content (children o component), renderizarlo
  if (content) {
    return <>{content}</>;
  }
  
  // Si no hay content, usar Outlet para subrutas anidadas
  return <Outlet />;
}
